import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { Button } from '@backstage/ui';
import {
  adminCreatePermission,
  adminUpdatePermission,
  adminDeletePermission,
  AccountEntity,
  CloudResourceEntity,
  getAccountSyncState,
  RuleEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ButtonIconDelete,
  ButtonIconEdit,
  LabelsContextProvider,
} from '@internal/backstage-plugin-devops-react';
import { DeleteAccountDialog } from '../DialogPanels';
import { EditAccountDrawer } from '../Drawers';
import { ProviderChip } from '../shared/ProviderChip';
import { EmptyState } from '@internal/backstage-plugin-devops-react';
import { useAccountsContext } from '../Providers';
import styles from './AccountsTab.module.css';

/**
 * Accounts tab of the project page, 1:1 with the accounts rollup in
 * design/project-overview-page-mockup.html — per-account resources,
 * compliance score (mini-bar), failed rules and sync state, rows deep-link
 * into the account pages. The admin Add/Edit/Delete flow of the old table
 * is kept, permission-gated as before.
 */
export function AccountsTab() {
  const { accounts, isLoading, selectAccount } = useAccountsContext();
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false);
  const [isAccountDeleteOpen, setIsAccountDeleteOpen] = useState(false);
  const { entity: project } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const { allowed: allowedCreate } = usePermission({
    permission: adminCreatePermission,
  });
  const { allowed: allowedUpdate } = usePermission({
    permission: adminUpdatePermission,
  });
  const { allowed: allowedDelete } = usePermission({
    permission: adminDeletePermission,
  });

  const projectAccounts = useMemo(
    () =>
      (accounts as AccountEntity[]).filter(
        account => account.spec.domain === project.metadata.name,
      ),
    [accounts, project],
  );
  const accountNames = projectAccounts.map(account => account.metadata.name);

  const [rules, setRules] = useState<RuleEntity[]>([]);
  const [resources, setResources] = useState<CloudResourceEntity[]>([]);

  useEffect(() => {
    if (accountNames.length === 0) {
      setRules([]);
      setResources([]);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'Rule',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setRules(items as RuleEntity[]);
        }
      })
      .catch(() => {
        // Score/failed columns degrade to zero.
      });
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'CloudResource',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setResources(items as CloudResourceEntity[]);
        }
      })
      .catch(() => {
        // Resource counts degrade to zero.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, project.metadata.name, accountNames.join('|')]);

  const rows = useMemo(
    () =>
      projectAccounts.map(account => {
        const accountRules = rules.filter(
          rule => rule.spec.account === account.metadata.name,
        );
        const scored = accountRules.filter(
          rule => rule.spec.result !== 'Unknown',
        );
        const passed = scored.filter(rule => rule.spec.result === 'Passed')
          .length;
        return {
          account,
          resources: resources.filter(
            resource => resource.spec.account === account.metadata.name,
          ).length,
          score: scored.length ? Math.round((passed / scored.length) * 100) : 0,
          failedCount: accountRules.filter(rule => rule.spec.result === 'Failed')
            .length,
          sync: getAccountSyncState(account),
        };
      }),
    [projectAccounts, rules, resources],
  );

  const scoreBarClass = (score: number): string => {
    if (score >= 80) {
      return styles.barSuccess;
    }
    if (score >= 50) {
      return styles.barWarning;
    }
    return styles.barDanger;
  };

  const syncPillClass = (tone: string): string => {
    if (tone === 'success') {
      return styles.pillPassed;
    }
    if (tone === 'warning') {
      return styles.pillWarning;
    }
    return styles.pillNeutral;
  };

  const syncPillLabel = (
    sync: ReturnType<typeof getAccountSyncState>,
  ): string => {
    if (sync.errorCount > 0) {
      return `${sync.errorCount} error${sync.errorCount > 1 ? 's' : ''}`;
    }
    if (sync.tone === 'success') {
      return 'healthy';
    }
    return sync.label;
  };

  const providerBadge = (provider: string) => (
    <span className={styles.pvWrap}>
      <ProviderChip provider={provider} />
    </span>
  );

  const openEdit = (account: AccountEntity) => {
    selectAccount(account);
    setIsAccountDrawerOpen(true);
  };

  const openDelete = (account: AccountEntity) => {
    selectAccount(account);
    setIsAccountDeleteOpen(true);
  };

  const withActions = allowedUpdate || allowedDelete;

  return (
    <>
      <div className={styles.topRow}>
        <span className={styles.topTitle}>Accounts</span>
        <span className={styles.topHint}>
          Compliance and inventory roll up from each account
        </span>
        {allowedCreate && (
          <Button
            className={styles.addButton}
            onClick={() => setIsAccountDrawerOpen(true)}
          >
            Add Account
          </Button>
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label="Accounts in this project">
          <thead>
            <tr>
              <th>Account</th>
              <th className={styles.num}>Resources</th>
              <th>Compliance</th>
              <th className={styles.num}>Failed rules</th>
              <th>Sync</th>
              {withActions && <th className={styles.actions}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const provider = (row.account.spec.type ?? '').toUpperCase();
              return (
                <tr key={row.account.metadata.name}>
                  <td>
                    <Link
                      className={styles.accName}
                      to={`/catalog/default/account/${row.account.metadata.name}`}
                    >
                      {providerBadge(provider)}
                      {row.account.metadata.title ?? row.account.metadata.name}
                      <span className={styles.accId}>
                        {row.account.metadata.name}
                      </span>
                    </Link>
                  </td>
                  <td className={styles.num}>{row.resources}</td>
                  <td>
                    <span className={styles.scorePill}>
                      <span className={styles.scoreBar}>
                        <i
                          className={scoreBarClass(row.score)}
                          style={{ width: `${row.score}%` }}
                        />
                      </span>
                      {row.score}
                    </span>
                  </td>
                  <td className={styles.num}>
                    {row.failedCount > 0 ? (
                      <span className={`${styles.pill} ${styles.pillFailed}`}>
                        {row.failedCount}
                      </span>
                    ) : (
                      <span className={styles.muted}>0</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`${styles.pill} ${syncPillClass(row.sync.tone)}`}
                    >
                      <span className={styles.pillDot} />
                      {syncPillLabel(row.sync)}
                    </span>
                  </td>
                  {withActions && (
                    <td className={styles.actions}>
                      {allowedUpdate && row.account.spec.dynamic_uuid && (
                        <ButtonIconEdit
                          variant="tertiary"
                          size="small"
                          isDisabled={isLoading}
                          aria-label={`Edit ${row.account.metadata.name}`}
                          onClick={() => openEdit(row.account)}
                        />
                      )}
                      {allowedDelete && row.account.spec.dynamic_uuid && (
                        <ButtonIconDelete
                          variant="tertiary"
                          size="small"
                          isDisabled={isLoading}
                          aria-label={`Delete ${row.account.metadata.name}`}
                          onClick={() => openDelete(row.account)}
                        />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    title="No accounts in this project yet"
                    hint="Accounts appear here after the provider ingests them into the catalog."
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DeleteAccountDialog
        isOpen={isAccountDeleteOpen}
        onClose={() => setIsAccountDeleteOpen(false)}
      />
      <LabelsContextProvider>
        <EditAccountDrawer
          isOpen={isAccountDrawerOpen}
          onClose={() => setIsAccountDrawerOpen(false)}
          project={project}
        />
      </LabelsContextProvider>
    </>
  );
}
